import { recoverPrimaryWaveHeight } from "./rideable-wave-partitions";
import type {
  SafetyExplanationSeverity,
  SafetyFactorExplanation,
  SportScoreExplanation,
} from "./factor-explanations";
import type { HourlyScore } from "./session-window-universal";
import type { SupProfileHourlyScore } from "./sup-profile-transport";

export type WeatherHourly = {
  // Temperature
  temp_c: number;
  apparent_temp_c: number | null;
  dew_point_c: number | null;
  wet_bulb_temp_c: number | null;
  temp_80m_c: number | null;
  temp_120m_c: number | null;
  temp_180m_c: number | null;

  // Moisture
  humidity_pct: number | null;
  vapour_pressure_deficit_kpa: number | null;

  // Precipitation
  precip_mm: number;
  rain_mm: number | null;
  showers_mm: number | null;
  snowfall_cm: number | null;
  snow_depth_m: number | null;
  precip_probability_pct: number | null;
  weather_code: number;

  // Cloud & sky
  cloud_cover_pct: number;
  cloud_cover_low_pct: number | null;
  cloud_cover_mid_pct: number | null;
  cloud_cover_high_pct: number | null;
  sunshine_duration_s: number | null;

  // Pressure
  pressure_msl_hpa: number | null;
  surface_pressure_hpa: number | null;

  // Wind -- surface
  wind_speed_10m_kmh: number;
  wind_dir_10m_deg: number;
  wind_gust_10m_kmh: number;
  // Wind -- higher altitudes
  wind_speed_80m_kmh: number | null;
  wind_dir_80m_deg: number | null;
  wind_speed_120m_kmh: number | null;
  wind_dir_120m_deg: number | null;
  wind_speed_180m_kmh: number | null;
  wind_dir_180m_deg: number | null;

  // Visibility & radiation
  visibility_m: number | null;
  uv_index: number;
  is_day: boolean;
  shortwave_radiation_wm2: number | null;
  direct_radiation_wm2: number | null;
  diffuse_radiation_wm2: number | null;
  direct_normal_irradiance_wm2: number | null;
  terrestrial_radiation_wm2: number | null;

  // Atmospheric
  freezing_level_m: number | null;
  cape_jkg: number | null;
  lifted_index: number | null;
  convective_inhibition_jkg: number | null;
  boundary_layer_height_m: number | null;

  // Evapotranspiration
  evapotranspiration_mm: number | null;
  et0_fao_mm: number | null;

  // Soil temperature
  soil_temp_0cm_c: number | null;
  soil_temp_6cm_c: number | null;
  soil_temp_18cm_c: number | null;
  soil_temp_54cm_c: number | null;

  // Soil moisture (m3/m3)
  soil_moisture_0_1cm: number | null;
  soil_moisture_1_3cm: number | null;
  soil_moisture_3_9cm: number | null;
  soil_moisture_9_27cm: number | null;
  soil_moisture_27_81cm: number | null;

  /** Optional snowfall water equivalent (mm), when fetched. Open-Meteo's snowfall_cm is a fixed 0.7 x this. */
  snowfall_water_equivalent_mm?: number | null;
  /** Optional pressure-level profile (ski v3 forcing). Absent in older stored rows. */
  pressure_levels?: readonly PressureLevelReading[];
};

export type PressureLevelReading = {
  hPa: number;
  temp_c: number | null;
  rh_pct: number | null;
  height_m: number | null;
  cloud_pct: number | null;
  /** Wind at the level (km/h), when fetched (700 hPa ridge wind). */
  wind_kmh?: number | null;
  wind_dir_deg?: number | null;
};

export type MarineHourly = {
  // Combined waves
  wave_height_m: number | null;
  wave_dir_deg: number | null;
  wave_period_s: number | null;
  wave_peak_period_s: number | null;

  // Wind waves
  wind_wave_height_m: number | null;
  wind_wave_dir_deg: number | null;
  wind_wave_period_s: number | null;
  wind_wave_peak_period_s: number | null;

  // Primary swell
  swell_height_m: number | null;
  swell_dir_deg: number | null;
  swell_period_s: number | null;
  swell_peak_period_s: number | null;

  // Secondary swell
  swell2_height_m: number | null;
  swell2_dir_deg: number | null;
  swell2_period_s: number | null;

  // Tertiary swell
  swell3_height_m: number | null;
  swell3_dir_deg: number | null;
  swell3_period_s: number | null;

  // Ocean
  sst_c: number | null;
  /** Open-Meteo returns ocean current velocity in km/h. */
  ocean_current_velocity_kmh?: number | null;
  /** Legacy key whose stored values also came from Open-Meteo in km/h. */
  ocean_current_velocity_ms: number | null;
  ocean_current_dir_deg: number | null;
  sea_level_height_m: number | null;
};

export type ScoringMarineReading = {
  swellWaveHeightM: number;
  swellWavePeriodS: number;
  swellWaveDirectionDeg: number | null;
  windWaveHeightM: number;
  windWaveDirectionDeg: number | null;
  windWavePeriodS: number | null;
  secondarySwellWaveHeightM: number | null;
  secondarySwellWavePeriodS: number | null;
  secondarySwellWaveDirectionDeg: number | null;
};

export function hasUsableMarineHeight(
  marine: MarineHourly | null | undefined,
): marine is MarineHourly {
  if (!marine) return false;
  return [
    marine.swell_height_m,
    marine.wave_height_m,
    marine.wind_wave_height_m,
  ].some((value) => typeof value === "number" && Number.isFinite(value) && value >= 0);
}

export function mapMarineForScoring(
  marine: MarineHourly | null | undefined,
): ScoringMarineReading | null {
  if (!hasUsableMarineHeight(marine)) return null;
  const valid = (value: number | null | undefined) =>
    typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
  const wind = valid(marine.wind_wave_height_m) ?? 0;
  // Combined Hs already includes the partitions. If it must supply a missing
  // primary, subtract their variance before scoring them separately.
  const recoveredPrimary = recoverPrimaryWaveHeight(
    marine.swell_height_m, marine.wave_height_m, wind, marine.swell2_height_m,
  ) ?? 0;
  return {
    swellWaveHeightM: recoveredPrimary,
    swellWavePeriodS: valid(marine.swell_period_s) ?? valid(marine.wave_period_s) ?? 0,
    swellWaveDirectionDeg: marine.swell_dir_deg ?? marine.wave_dir_deg ?? null,
    windWaveHeightM: wind,
    windWaveDirectionDeg: marine.wind_wave_dir_deg ?? null,
    windWavePeriodS: valid(marine.wind_wave_period_s),
    secondarySwellWaveHeightM: marine.swell2_height_m,
    secondarySwellWavePeriodS: marine.swell2_period_s,
    secondarySwellWaveDirectionDeg: marine.swell2_dir_deg,
  };
}

export function storedOceanCurrentMs(
  marine: MarineHourly | null | undefined,
): number | null {
  if (!marine) return null;
  const storedKmh = marine.ocean_current_velocity_kmh ?? marine.ocean_current_velocity_ms;
  return oceanCurrentKmhToMs(storedKmh);
}

/** Validate and convert raw Open-Meteo current evidence from km/h to m/s. */
export function oceanCurrentKmhToMs(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value / 3.6
    : null;
}

/** Validate a value which is already in canonical metres per second. */
export function canonicalCurrentVelocityMs(
  value: number | null | undefined,
): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

export type SourcePresence = {
  weather: {
    windSpeed: boolean;
    windDirection: boolean;
    windGust: boolean;
    precipitation: boolean;
    cloudCover: boolean;
    visibility: boolean;
    weatherCode: boolean;
    /**
     * False when the provider returned no temperature (written only when false, so absent
     * means present). The ski normaliser writes it inside its compact presence; the coastal
     * normaliser adds it to its recorded presence (all-sports engine design 2.5).
     */
    temperature?: boolean;
    /** Ski normaliser only: false when the provider returned no shortwave radiation. */
    radiation?: boolean;
    /** Coastal normaliser only: false when the provider returned no UV index (absent means present). */
    uvIndex?: boolean;
    /** Coastal normaliser only: false when the provider returned no is_day flag (absent means present). */
    isDay?: boolean;
  };
  marine: {
    waveHeight: boolean;
    wavePeriod: boolean;
    swellHeight: boolean;
    swellPeriod: boolean;
    swellDirection: boolean;
    windWaveHeight: boolean;
    windWaveDirection: boolean;
    windWavePeriod: boolean;
    currentVelocity: boolean;
    currentDirection: boolean;
  };
};

export type NormalisedHour = {
  hour: number;
  weather: WeatherHourly;
  marine: MarineHourly | null;
  source_presence?: SourcePresence;
};

export type DailySources = {
  copernicus?: {
    secchi_depth_m: number | null;
    chlorophyll_mg_m3: number | null;
    spm_g_m3: number | null;
    fetched_at: string;
  };
  water_quality?: {
    secchi_depth_m: number | null;
    chlorophyll_mg_m3: number | null;
    spm_g_m3: number | null;
    fetched_at: string;
  };
  avalanche?: {
    danger_level: number | null;
    problems: string[];
    elevation_band_m: number | null;
    bulletin_date: string;
    fetched_at: string;
  };
  bathing_water?: {
    classification: string | null;
    e_coli_cfu_100ml: number | null;
    intestinal_enterococci_cfu_100ml: number | null;
    source: string;
    sample_date: string | null;
    fetched_at: string;
  };
};

export type SourceVersions = {
  weather: string;
  marine?: string;
  snow_depth?: string;
};

/**
 * Phase-3 multi-elevation natural snow depths (metres) for a ski resort on this
 * day, fetched separately from the single-elevation per-hour `snow_depth_m`.
 * The native cell is the keyless Open-Meteo grid-cell fetch (no `elevation=`
 * param); `nativeElevM` is the elevation that cell reports back. Any band may be
 * null (missing column or a failed/skipped fetch) — `bandDepthsToAnchors`
 * degrades to fewer interpolation anchors / the B1 fallback. Stored ALONGSIDE
 * the raw hourly depth, never replacing it.
 */
export type BandDepths = {
  baseM: number | null;
  midM: number | null;
  summitM: number | null;
  nativeM: number | null;
  nativeElevM: number | null;
  fetchedAt: string;
  /** At most 24 local hours, with validated depths in base/mid/summit/native order. */
  hourly?: [hour: number, baseM: number | null, midM: number | null, summitM: number | null, nativeM: number | null][];
};

export type HourlyDataJsonb = {
  /** Forecast until the calendar day closes; then frozen as modelled past. */
  data_kind?: "forecast" | "modelled-past";
  hours: NormalisedHour[];
  hours_base?: NormalisedHour[];
  daily_sources?: DailySources;
  sources: SourceVersions;
  /** A complete alternate meteorological series; regional snow depth stays separate. */
  weather_fallback?: {
    reason: "missing-primary-weather";
    missingFields: string[];
  };
  band_depths?: BandDepths;
  /** Compact end-of-day model members at representative elevations, never per-run curves. */
  snow_state?: readonly import("./snow-estimate").SnowStateCheckpoint[];
  snow_depth_anchor?: {
    date: string;
    availableAt?: string;
    depthM: number;
    network: string;
    stationId: string;
    distanceKm: number;
    elevationDeltaM: number;
    responseSha256: string;
    /** Only an explicit managed-piste source may populate this measurement. */
    managedPiste?: {
      scope: "representative-pistes";
      rangeM: readonly [number, number];
      sourceUrl: string;
    };
  };
};

export type ObservationSummaries = {
  total_precip_mm: number | null;
  total_rain_mm: number | null;
  total_snowfall_cm: number | null;
  max_temp_c: number | null;
  min_temp_c: number | null;
  mean_temp_c: number | null;
  max_wind_speed_kmh: number | null;
  max_wind_gust_kmh: number | null;
  mean_wind_kmh: number | null;
  dominant_wind_dir_deg: number | null;
  total_sunshine_hrs: number | null;
  mean_cloud_cover_pct: number | null;
  weather_code: number | null;
  min_visibility_m: number | null;
  max_visibility_m: number | null;
  mean_pressure_hpa: number | null;
  max_uv_index: number | null;
  mean_sst_c: number | null;
  max_swell_m: number | null;
  mean_swell_period_s: number | null;
  max_wave_height_m: number | null;
  snow_depth_m: number | null;
  freezing_level_m: number | null;
};

export type NamedSupStoredScores = {
  sup_touring_score?: number | null;
  sup_touring_label?: string | null;
  sup_touring_safety?: unknown | null;
  sup_surf_score?: number | null;
  sup_surf_label?: string | null;
  sup_surf_safety?: unknown | null;
  sup_downwind_score?: number | null;
  sup_downwind_label?: string | null;
  sup_downwind_safety?: unknown | null;
};

export type NamedSupObservationStorage = {
  sup_touring_score?: number | null;
  sup_touring_label?: string | null;
  sup_surf_score?: number | null;
  sup_surf_label?: string | null;
  sup_downwind_score?: number | null;
  sup_downwind_label?: string | null;
};

export type SportScores = {
  surf_score: number | null;
  surf_label: string | null;
  wind_score: number | null;
  wind_label: string | null;
  foil_score: number | null;
  foil_label: string | null;
  diving_score: number | null;
  diving_label: string | null;
  swim_score: number | null;
  swim_label: string | null;
  sup_score: number | null;
  sup_label: string | null;
  ski_piste_score: number | null;
  ski_piste_label: string | null;
  ski_offpiste_score: number | null;
  ski_offpiste_label: string | null;
  mtb_score: number | null;
  mtb_label: string | null;
  ski_touring_score: number | null;
  ski_touring_label: string | null;
  sailing_club_score: number | null;
  sailing_club_label: string | null;
  sailing_hp_score: number | null;
  sailing_hp_label: string | null;
  bodyboarding_score?: number | null;
  bodyboarding_label?: string | null;
  downwind_foil_score?: number | null;
  downwind_foil_label?: string | null;
  wing_foil_score?: number | null;
  wing_foil_label?: string | null;
} & NamedSupStoredScores;

export type StoredHourlyScore = HourlyScore | SupProfileHourlyScore;

type StoredSportScoreExplanation<
  TPoint extends StoredHourlyScore,
> = [Extract<TPoint, SupProfileHourlyScore>] extends [never]
  ? SportScoreExplanation
  : SportScoreExplanation<SafetyFactorExplanation<SafetyExplanationSeverity>>;

/**
 * One candidate window of a sailing formula after revision 1 (P2C-WP9-13): local hours, end
 * exclusive, the window's score on the 0 to 10 strip scale (one decimal), `red` only when an hour
 * of the window carries a red row of the kernel's window rule (SAIL-6), and `best` on exactly one
 * window per list, the kernel's best window (the headline's), which one decimal can tie with a
 * neighbour.
 */
export type StoredCandidateWindow = {
  startHour: number;
  endHour: number;
  score: number;
  red?: true;
  best?: true;
};

/** One sport's candidate windows of a day, with the revision that scored them (its stamp). */
export type StoredCandidateWindows = {
  revision: number;
  windows: StoredCandidateWindow[];
};

export type PrecomputedSportScores<
  TPoint extends StoredHourlyScore = HourlyScore,
> = {
  sportScoringVersions?: import("./sport-scoring-versions").SportScoringVersions;
  sportHourlyScores: Record<string, TPoint[]>;
  /** Bounded producer reasons stored once per sport/day, never per hour. */
  sportConfidenceReasons?: Record<string, string[]>;
  sportScoreExplanations?: Record<string, StoredSportScoreExplanation<TPoint>>;
  /** Exact canonical starts from the unrounded daily scorers. */
  bestWindowStarts?: Record<string, number>;
  /**
   * Sailing only, a formula after revision 1 on days 0 and 1: every candidate window the kernel
   * scored, for the session planner (P2C-WP9-13). Never written for revision 1 or its alias.
   */
  sportCandidateWindows?: Record<string, StoredCandidateWindows>;
  universalEnhancements: Record<string, unknown>;
  surfEnhancements?: unknown;
  windEnhancements?: unknown;
  divingEnhancements?: unknown;
  swimEnhancements?: unknown;
  supEnhancements?: unknown;
  offPisteEnhancements?: unknown;
  touringEnhancements?: unknown;
  mtbEnhancements?: unknown;
};

export type SupProfilePrecomputedSportScores =
  PrecomputedSportScores<StoredHourlyScore>;

export type DailyObservationRow<
  TPoint extends StoredHourlyScore = HourlyScore,
> = {
  location_id: string;
  date: string;
  is_actual: boolean;
  hourly_data: HourlyDataJsonb;
  sport_hourly_scores?: PrecomputedSportScores<TPoint> | null;
  score_provenance?: Record<string, unknown>;
  // Algorithm version the row's scores were computed with. See
  // lib/conditions/scoring-versions.ts.
  scoring_version?: number;
  fetched_at: string;
  updated_at: string;
} & SportScores
  & NamedSupObservationStorage
  & ObservationSummaries;
